import { useRegisterSW } from 'virtual:pwa-register/react'
import { HTTP_STATUS } from '@quadralize/utils'
import { useEffect } from 'react'

/**
 * Interval in milliseconds to check for SW updates.
 * Aggressive (60s) to ensure users always get fresh deploys quickly.
 */
const SW_UPDATE_CHECK_INTERVAL_MS = 60_000

/**
 * Hook to manage PWA service worker registration and updates.
 *
 * Strategy: `registerType: 'prompt'` — the new SW is installed in the background,
 * but does NOT activate until the user explicitly confirms the update.
 * This prevents breaking the running app while guaranteeing fresh content on next load.
 *
 * Safety mechanisms:
 * - Periodic SW check every 60s (catches new deploys even on long-lived tabs)
 * - Auto-activates new SW when the tab becomes hidden (user switches tabs/minimizes)
 * - Auto-activates new SW on page unload (refresh/close), so next load is fresh
 * - Manual "Atualizar" button for immediate update
 * - Network-aware: skips checks when offline
 * - Cache-busting fetch headers on SW script to bypass CDN/browser HTTP cache
 */
export function usePwaUpdate(): PwaUpdateState {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swScriptUrl, registration) {
      if (!registration) return

      setInterval(async () => {
        // Skip if a new SW is already installing
        if (registration.installing) return

        // Skip when no network connection available
        if ('connection' in navigator && !navigator.onLine) return

        try {
          const response = await fetch(swScriptUrl, {
            cache: 'no-store',
            headers: {
              cache: 'no-store',
              'cache-control': 'no-cache',
            },
          })

          if (response.status === HTTP_STATUS.OK) {
            await registration.update()
          }
        } catch {
          // Network error — silently ignore, will retry on next interval
        }
      }, SW_UPDATE_CHECK_INTERVAL_MS)
    },
    onRegisterError(error) {
      console.error('[PWA] Service Worker registration failed:', error)
    },
  })

  // Auto-activate the waiting SW when user leaves or hides the tab.
  // This ensures the next page load uses the latest version without user interaction.
  useEffect(() => {
    if (!needRefresh) return

    const activateOnHidden = (): void => {
      if (document.visibilityState === 'hidden') {
        // Activate without reload — next navigation will use the new SW
        updateServiceWorker(false)
      }
    }

    const activateOnUnload = (): void => {
      // Activate before page closes/reloads so the next load is fresh
      updateServiceWorker(false)
    }

    document.addEventListener('visibilitychange', activateOnHidden)
    window.addEventListener('beforeunload', activateOnUnload)

    return () => {
      document.removeEventListener('visibilitychange', activateOnHidden)
      window.removeEventListener('beforeunload', activateOnUnload)
    }
  }, [needRefresh, updateServiceWorker])

  return { needRefresh, updateServiceWorker }
}

interface PwaUpdateState {
  /** Whether a new version is ready and waiting for user confirmation */
  needRefresh: boolean
  /** Activates the waiting SW and reloads the page when `true` is passed */
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>
}
